import React, { useState, useEffect, useRef } from 'react';
import { List, ChevronDown, ChevronUp, Hash } from 'lucide-react';

/**
 * Enhanced Table of Contents Component
 * Features:
 * - H2-only mode for cleaner TOC (SEO optimized)
 * - Anchor links for Google snippets
 * - Sticky sidebar on desktop
 * - Active section highlighting
 * - Click-to-scroll navigation
 */
export const TableOfContents = ({ content, className = '', h2Only = true }) => {
  const [headings, setHeadings] = useState([]);
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeId, setActiveId] = useState('');

  // Parse headings from HTML content and add IDs
  useEffect(() => {
    if (!content) return;
    
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    
    const selector = h2Only ? 'h2' : 'h2, h3';
    const elements = tempDiv.querySelectorAll(selector);
    
    const items = Array.from(elements).map((heading, index) => {
      const text = heading.textContent || '';
      // Clean text: remove emojis, extra spaces
      const cleanText = text.replace(/^[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}]/gu, '').trim();
      // Generate SEO-friendly anchor ID
      const id = `section-${cleanText.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 50)}`;
      
      return {
        id,
        text: cleanText,
        originalText: text,
        level: heading.tagName === 'H2' ? 2 : 3,
        index
      };
    });
    
    setHeadings(items);
    
    // Apply IDs to actual DOM elements after render
    setTimeout(() => {
      const articleContent = document.querySelector('.article-content');
      if (articleContent) {
        const domHeadings = articleContent.querySelectorAll(selector);
        domHeadings.forEach((heading, index) => {
          if (items[index]) {
            heading.id = items[index].id;
          }
        });
      }
    }, 100);
  }, [content, h2Only]);

  // Track active heading on scroll
  useEffect(() => {
    if (headings.length === 0) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: '-100px 0px -70% 0px', threshold: 0 }
    );

    headings.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [headings]);

  // Scroll to heading with smooth animation
  const scrollToHeading = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const top = element.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
      
      // Update URL hash for sharing
      window.history.replaceState(null, '', `#${id}`);
    }
  };

  // Handle initial hash on page load
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash) {
      setTimeout(() => scrollToHeading(hash), 500);
    }
  }, []);

  if (headings.length < 2) return null;

  return (
    <div className={`toc-container ${className}`} data-testid="table-of-contents">
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        {/* Header */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-5 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
          aria-expanded={isExpanded}
        >
          <div className="flex items-center gap-2">
            <List className="w-5 h-5 text-brand-red" />
            <span className="font-bold text-gray-900">In This Article</span>
            <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full ml-2">
              {headings.length} sections
            </span>
          </div>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {/* TOC List */}
        {isExpanded && (
          <nav className="p-4" aria-label="Table of contents">
            <ol className="space-y-1 list-none m-0 p-0">
              {headings.map((heading, index) => (
                <li key={heading.id} className="m-0 p-0">
                  <a
                    href={`#${heading.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToHeading(heading.id);
                    }}
                    className={`flex items-start gap-2 py-2 px-3 rounded-lg transition-all text-sm group ${
                      heading.level === 3 ? 'pl-6' : ''
                    } ${
                      activeId === heading.id
                        ? 'bg-brand-red/10 text-brand-red font-semibold'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                    data-testid={`toc-item-${index}`}
                  >
                    <Hash className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                      activeId === heading.id ? 'text-brand-red' : 'text-gray-400 group-hover:text-gray-600'
                    }`} />
                    <span className="line-clamp-2">{heading.text}</span>
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        )}
      </div>
    </div>
  );
};

/**
 * Sticky TOC for desktop sidebar
 */
export const StickyTableOfContents = ({ content, h2Only = true }) => {
  const [isSticky, setIsSticky] = useState(false);
  const tocRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (tocRef.current) {
        const rect = tocRef.current.getBoundingClientRect();
        setIsSticky(rect.top <= 100);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div ref={tocRef} className={`hidden lg:block transition-all duration-300 ${isSticky ? 'sticky top-24' : ''}`}>
      <TableOfContents content={content} h2Only={h2Only} />
    </div>
  );
};

/**
 * Inline TOC (expandable, shown at top of article)
 */
export const InlineTableOfContents = ({ content, h2Only = true }) => {
  return (
    <div className="my-8">
      <TableOfContents content={content} h2Only={h2Only} />
    </div>
  );
};

/**
 * Floating TOC (fixed position on large screens)
 */
export const FloatingTableOfContents = ({ content, h2Only = true }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!isVisible) return null;

  return (
    <div className="fixed right-6 top-1/2 -translate-y-1/2 z-40 hidden xl:block w-64">
      {isMinimized ? (
        <button
          onClick={() => setIsMinimized(false)}
          className="bg-white rounded-full p-3 shadow-lg border border-gray-200 hover:bg-gray-50"
          title="Show table of contents"
        >
          <List className="w-5 h-5 text-brand-red" />
        </button>
      ) : (
        <div className="relative">
          <button
            onClick={() => setIsMinimized(true)}
            className="absolute -top-2 -right-2 bg-gray-200 rounded-full p-1 hover:bg-gray-300 z-10"
            title="Minimize"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
          <TableOfContents content={content} h2Only={h2Only} />
        </div>
      )}
    </div>
  );
};

export default TableOfContents;
